import { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, Gauge } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { PartnerMemberQuotaRow } from '@/hooks/useEnterpriseEmailMonitoring';

interface MemberQuotaPanelProps {
  quotas: PartnerMemberQuotaRow[];
  partnerId: string;
}

/**
 * M2 (#572): today's per-account daily-quota consumption across a partner's
 * workspaces. Rows are sorted by consumption (highest first); accounts at or
 * above the near-wall fraction get a yellow flag so an admin can see who is
 * about to hit their daily sending limit.
 */
export function MemberQuotaPanel({
  quotas,
  partnerId,
}: MemberQuotaPanelProps): JSX.Element {
  const sorted = useMemo(
    () => [...quotas].sort((a, b) => b.pct_consumed - a.pct_consumed),
    [quotas],
  );

  return (
    <Card data-partner-id={partnerId}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge className="h-5 w-5" />
          Daily sending limits
        </CardTitle>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <div>No quota data</div>
            <div className="text-sm">
              No sender accounts with daily limits across these workspaces yet.
            </div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Workspace</TableHead>
                <TableHead>Sender</TableHead>
                <TableHead className="text-right">Sent today / Limit</TableHead>
                <TableHead className="w-40">Consumed</TableHead>
                <TableHead className="text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((q) => {
                const pct = Math.round(q.pct_consumed * 100);
                return (
                  <TableRow key={`${q.workspace_id}:${q.email_address}`}>
                    <TableCell className="text-sm text-muted-foreground">
                      {q.workspace_name}
                    </TableCell>
                    <TableCell className="text-sm font-medium">{q.email_address}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {q.emails_sent_today} / {q.daily_limit}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={Math.min(pct, 100)} className="h-2" />
                        <span className="w-10 text-right text-xs tabular-nums text-muted-foreground">
                          {pct}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {q.near_wall ? (
                        <Badge
                          variant="default"
                          className="gap-1 bg-yellow-500 hover:bg-yellow-500"
                        >
                          <AlertTriangle className="h-3 w-3" />
                          Near limit
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-green-100 text-green-700">
                          OK
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
